
import React, { useContext, useState } from 'react';
import { AppContext } from '../App';
import { Project, Task } from '../types';
import { supabase } from '../supabase';

const Projects: React.FC = () => {
  const context = useContext(AppContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);

  if (!context) return null;
  const { projects, clients, searchTerm, tasks, syncAllData } = context;

  const filteredProjects = projects.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.client.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSaveProject = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const projectData = {
      name: formData.get('name') as string,
      client: formData.get('client') as string,
      deadline: formData.get('deadline') as string,
      completion: parseInt(formData.get('completion') as string || '0'),
      status: formData.get('status') as any || 'Planning'
    };

    try {
      if (editingProject) {
        const { error } = await supabase
          .from('projects')
          .update(projectData)
          .eq('id', editingProject.id);
        if (error) throw error;
      } else {
        const newProject: Project = {
          id: `P${Date.now()}`,
          ...projectData,
          status: 'Planning',
          completion: 0
        };
        const { error } = await supabase.from('projects').insert(newProject);
        if (error) throw error;
      }
      await syncAllData();
      closeModal();
    } catch (err: any) {
      alert(err.message || 'Erro ao salvar projeto.');
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Excluir este projeto permanentemente?')) {
      try {
        const { error } = await supabase.from('projects').delete().eq('id', id);
        if (error) throw error;
        await syncAllData();
      } catch (err: any) {
        alert(err.message || 'Erro ao excluir projeto.');
      }
    }
  };

  const openEdit = (project: Project) => {
    setEditingProject(project);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProject(null);
  };

  return (
    <div className="p-8 max-w-7xl mx-auto flex flex-col gap-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-black text-text-main tracking-tight">Meus Projetos</h2>
          <p className="text-text-secondary font-medium mt-1">Acompanhamento de entregas e fluxos vinculados.</p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="h-12 px-6 rounded-xl bg-primary text-white text-sm font-black shadow-xl shadow-primary/20 hover:scale-105 transition-all flex items-center gap-2"
        >
          <span className="material-symbols-outlined">add</span>
          Novo Projeto
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProjects.map((project) => {
          const projectTasks = tasks.filter(t => t.projectId === project.id);
          const completedTasks = projectTasks.filter(t => t.status === 'Done');

          return (
            <div key={project.id} className="bg-white p-6 rounded-[23px] border border-gray-50 shadow-sm hover:border-primary/20 transition-all group flex flex-col h-fit">
              <div className="flex justify-between items-start mb-4">
                <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${project.status === 'Active' ? 'bg-primary/10 text-primary' :
                  project.status === 'Review' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                  {project.status}
                </span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(project)} className="size-8 flex items-center justify-center rounded-lg text-text-secondary hover:bg-gray-100 hover:text-primary transition-all">
                    <span className="material-symbols-outlined text-[18px]">edit</span>
                  </button>
                  <button onClick={() => handleDelete(project.id)} className="size-8 flex items-center justify-center rounded-lg text-text-secondary hover:bg-red-50 hover:text-red-600 transition-all">
                    <span className="material-symbols-outlined text-[18px]">delete</span>
                  </button>
                </div>
              </div>
              <h3 className="text-lg font-black text-text-main group-hover:text-primary transition-colors">{project.name}</h3>
              <p className="text-xs text-text-secondary font-bold mt-1 uppercase tracking-widest">{project.client}</p>

              <div className="mt-6">
                <div className="flex justify-between items-end mb-2">
                  <span className="text-[10px] font-black text-text-secondary uppercase tracking-widest">Progresso do Projeto</span>
                  <span className="text-xs font-black text-primary">{project.completion}%</span>
                </div>
                <div className="w-full bg-gray-50 h-2 rounded-full overflow-hidden">
                  <div className="bg-primary h-full transition-all duration-500" style={{ width: `${project.completion}%` }}></div>
                </div>
              </div>

              {/* Lista de Atividades Vinculadas */}
              {projectTasks.length > 0 && (
                <div className="mt-6 flex flex-col gap-3">
                  <p className="text-[10px] font-black text-text-secondary uppercase tracking-[0.2em]">Fluxos Vinculados ({completedTasks.length}/{projectTasks.length})</p>
                  <div className="flex flex-col gap-2 max-h-32 overflow-y-auto scrollbar-hide pr-1">
                    {projectTasks.map(task => (
                      <div key={task.id} className="flex items-center justify-between p-2 rounded-xl bg-gray-50 border border-gray-100">
                        <span className={`text-[11px] font-bold truncate ${task.status === 'Done' ? 'text-gray-400 line-through' : 'text-text-main'}`}>
                          {task.name}
                        </span>
                        <span className={`text-[9px] font-black px-2 py-0.5 rounded-md uppercase ${task.status === 'Done' ? 'bg-green-100 text-green-600' : 'bg-primary-light text-primary'
                          }`}>
                          {task.status === 'Done' ? 'Done' : task.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-2 text-text-secondary">
                  <span className="material-symbols-outlined text-[18px]">calendar_today</span>
                  <span className="text-[10px] font-black uppercase tracking-widest">{project.deadline}</span>
                </div>
                <div className="flex -space-x-2">
                  <div className="size-7 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-[8px] font-black text-text-secondary overflow-hidden">
                    <span className="material-symbols-outlined text-[16px]">person</span>
                  </div>
                  <div className="size-7 rounded-full bg-primary-light border-2 border-white flex items-center justify-center text-[8px] font-black text-primary">
                    +{projectTasks.length}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-text-main/40 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-md rounded-[23px] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between shrink-0 bg-white">
              <h3 className="text-xl font-black text-text-main">{editingProject ? 'Editar Projeto' : 'Novo Projeto'}</h3>
              <button onClick={closeModal} className="text-text-secondary hover:text-primary"><span className="material-symbols-outlined">close</span></button>
            </div>
            <form onSubmit={handleSaveProject} className="p-6 flex flex-col gap-4 overflow-y-auto">
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-text-secondary">Nome do Projeto</label>
                <input name="name" defaultValue={editingProject?.name} required className="h-11 px-4 rounded-xl border-2 border-gray-200 bg-gray-50 outline-none text-sm focus:border-primary/20 transition-all" placeholder="Ex: App Mobile" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-text-secondary">Cliente</label>
                <select name="client" defaultValue={editingProject?.client} className="h-11 px-4 rounded-xl border-2 border-gray-200 bg-gray-50 outline-none text-sm focus:border-primary/20 transition-all">
                  {clients.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-text-secondary">Prazo</label>
                  <input name="deadline" defaultValue={editingProject?.deadline} required className="h-11 px-4 rounded-xl border-2 border-gray-200 bg-gray-50 outline-none text-sm focus:border-primary/20 transition-all" placeholder="Ex: 10 Jan" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-text-secondary">Status</label>
                  <select name="status" defaultValue={editingProject?.status} className="h-11 px-4 rounded-xl border-2 border-gray-200 bg-gray-50 outline-none text-sm focus:border-primary/20 transition-all">
                    <option value="Planning">Planejamento</option>
                    <option value="Active">Ativo</option>
                    <option value="Review">Revisão</option>
                  </select>
                </div>
              </div>
              <button type="submit" className="mt-4 h-12 rounded-xl bg-primary text-white font-black uppercase tracking-widest shadow-xl shadow-primary/20 transition-all shrink-0">
                {editingProject ? 'Salvar Projeto' : 'Criar Projeto'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;
